import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Icon,
  type NestedListRowData,
  NestedListRowGroup,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { CF_NEU_MED, CF_SEC_BLD, CF_SUS_BLD } from '../../constants';
import UiWrapper, { WRAPPER_MODE } from '../UiWrapper';

import CustomNestedRow from './CustomNestedRow';
import {
  FunctionCenterContent,
  SodSeverityChip,
  ViolationCenterContent,
} from './Shared';

import './Sod.css';

interface RiskEntitlements {
  Role: string;
  APPLICATION: string;
  EntitlementDisplayName: string;
  ParentRole: string;
  EntitlementDescription: string;
  Entitlement: string;
}

interface RiskFunctions {
  FunctionName: string;
  Description: string;
  Entitlements: RiskEntitlements[];
}

interface SodRisk {
  RiskId: number;
  Description: string;
  Priority: number;
  Risk: string;
  Ruleset?: string | null;
  RiskScore: number | null;
  Functions?: RiskFunctions[];
  Available_MitigatingControls: [];
  Assigned_MitigatingControls: [];
}

interface DataState {
  errorCode: string;
  message: string;
  result: { SOD: SodRisk[] };
}

interface SodProps {
  id: string;
  isLast?: boolean;
  isFullView?: boolean;
  isRightWindowOpen?: boolean;
  isChanged?: boolean;
  resetKey?: string;
  isViewing?: boolean;
  data?: object;
  onExpand?: () => void;
  onInlineExpand?: () => void;
  onCollapse?: () => void;
}

/**
 * Sod
 */
function Sod({
  id,
  isLast,
  isFullView,
  isViewing,
  isRightWindowOpen,
  data,
  isChanged,
  resetKey,
  onInlineExpand,
  onExpand,
  onCollapse,
}: SodProps) {
  const [currentMode, setCurrenMode] = useState(
    isFullView ? WRAPPER_MODE.EXPANDED : WRAPPER_MODE.INLINE
  );
  const [listRows, setListRows] = useState<NestedListRowData[]>([]);
  // Api utils

  const isZeroCount = useMemo(() => listRows.length === 0, [listRows.length]);

  useEffect(() => {
    const typedData = data as DataState;

    setListRows(
      typedData.result.SOD.map((risk) => ({
        id: risk.RiskId,
        data: {
          ...risk,
          tracker: {
            id: risk.RiskId,
            level: 0,
            type: 'Risk',
          },
        },
        isNestedRowExpanded: true,
        isCheckboxDisabled: true,
        leadingComp: (
          <Box tag='div' className='Sod-ViolationRisk-icon'>
            <Icon kind='SOD' size='medium' />
          </Box>
        ),

        centerContent: (
          <ViolationCenterContent
            riskName={risk.Risk}
            rulesetName={risk.Ruleset ?? 'This is Dummy ruleset'}
          />
        ),
        trailingContent: (
          <Box tag='div' className='Sod-ViolationRisk-chip'>
            <SodSeverityChip
              priority={String(risk.Priority)}
              riskScore={risk.RiskScore}
            />
          </Box>
        ),
        handleExpandToggler: undefined,
        className: isFullView
          ? 'Sod-ViolationRisk--isFullView'
          : 'Sod-ViolationRisk--isInline',
        childRows:
          risk.Functions && risk.Functions.length > 0
            ? risk.Functions.map((fn) => ({
                id: `${risk.RiskId}-${fn.FunctionName}`,
                data: {
                  ...fn,
                  tracker: {
                    id: fn.FunctionName,
                    level: 1,
                    type: 'Function',
                  },
                },
                isCheckboxDisabled: true,
                leadingComp: (
                  <Box tag='div' className='Sod-ViolationRisk-icon'>
                    <Icon kind='JobFunction' size='medium' />
                  </Box>
                ),
                centerContent: (
                  <FunctionCenterContent
                    fnName={fn.FunctionName}
                    accountName='WIP: Logo and name'
                    accountLogo='SAP'
                  />
                ),
                handleExpandToggler: undefined,
                className: isFullView
                  ? 'Sod-ViolationFunction--isFullView'
                  : 'Sod-ViolationFunction--isInline',
                childRows: undefined,
              }))
            : undefined,
      }))
    );
  }, [isFullView, data]);

  // Utils
  useEffect(() => {
    if (isFullView) {
      setCurrenMode(WRAPPER_MODE.EXPANDED);

      return;
    }

    if (isViewing) {
      setCurrenMode(WRAPPER_MODE.VIEWING);

      return;
    }

    if (isLast) {
      setCurrenMode(WRAPPER_MODE.INLINE);

      return;
    }

    setCurrenMode(WRAPPER_MODE.INLINE_COLLAPSED);
  }, [isFullView, isLast, isViewing, isChanged, resetKey]);

  const handleWrapperExpand = useCallback(() => {
    if (currentMode === WRAPPER_MODE.INLINE_COLLAPSED) {
      setCurrenMode(WRAPPER_MODE.INLINE);

      onInlineExpand?.();

      return;
    }

    if (currentMode === WRAPPER_MODE.INLINE && onExpand) {
      setCurrenMode(WRAPPER_MODE.VIEWING);

      onExpand();

      return;
    }

    setCurrenMode(WRAPPER_MODE.INLINE_COLLAPSED);
  }, [currentMode, onExpand, onInlineExpand]);

  if (!data || (data as DataState).errorCode !== '0') {
    return null;
  }

  if (isZeroCount) {
    return (
      <Box tag='div' className='Sod-wrapper--noData'>
        <Icon kind='SOD' size='medium' />
        <Typography kind='body1-bold' color={CF_SUS_BLD} lineLimit={1} isNoWrap>
          <FormattedMessage
            id='U0QXq+'
            defaultMessage='No SOD violations found'
            description='Text to indicate that no SoD violations were found for the request in the Sod component when the list is empty'
          />
        </Typography>
      </Box>
    );
  }

  return (
    <UiWrapper
      id={id}
      isActive={Boolean(isLast)}
      isRightWindowOpen={Boolean(isRightWindowOpen)}
      isEdited={isChanged}
      mode={currentMode}
      title={`SoD Conflict${listRows.length > 1 ? 's' : ''}`}
      isExpandDisabled={currentMode === WRAPPER_MODE.INLINE}
      onExpand={handleWrapperExpand}
      onCollapse={onCollapse}
      onClose={() => {
        setCurrenMode(WRAPPER_MODE.INLINE_COLLAPSED);
      }}
      className='Sod-wrapper'>
      {isFullView && (
        <Box tag='div' className='Sod-headerWrapper'>
          <Icon
            kind='StatusToDo'
            color='--color-foreground-warning-subtle'
            size='large'
          />
          <Box tag='div'>
            <Typography kind='h2' color={CF_SEC_BLD}>
              <FormattedMessage
                id='kp5pFb'
                defaultMessage='{count, plural, one {SoD Conflict} other {SoD Conflicts}}'
                values={{ count: listRows.length }}
              />
            </Typography>
            <Typography kind='body2' color={CF_NEU_MED}>
              <FormattedMessage
                id='sQfSnM'
                defaultMessage='Resolve any SoD violation conflicts with your request before
              continuing.'
                description='Text to instruct users to resolve any SoD violation conflicts before continuing with their request in the Sod component'
              />
            </Typography>
          </Box>
        </Box>
      )}
      <Box
        tag='div'
        className={cx(
          'Sod-bodyWrapper',
          isFullView && 'Sod-bodyWrapper--isFullView'
        )}>
        {isFullView ? (
          <NestedListRowGroup
            rowsData={listRows}
            setRowsData={setListRows}
            // selectedRowData={selectedRowData}
            setSelectedRowData={() => {}}
            tallestRowHeight={5.5}
            afterContentHeight={2.5}
          />
        ) : (
          <CustomNestedRow rowsData={listRows} />
        )}
      </Box>
    </UiWrapper>
  );
}

Sod.defaultProps = {
  isLast: true,
  isFullView: false,
  isViewing: false,
  isRightWindowOpen: false,
  isChanged: false,
  resetKey: '',
  onInlineExpand: undefined,
  onExpand: undefined,
  onCollapse: undefined,
  data: undefined,
};

export default memo(Sod);
